import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    MenuItem,
    IconButton,
} from "@mui/material";

import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";

import { db, storage } from "../firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

/* ---------------- CONSTANTS ---------------- */

const INDIAN_STATES = [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa",
    "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
    "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
    "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
    "Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir",
    "Ladakh","Puducherry","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
    "Lakshadweep","Andaman and Nicobar Islands"
];

const PROPERTY_TYPES = ["Hotel", "Bhaktniwas", "Lodge", "Resort", "Villa"];

/* ---------------- COMPONENT ---------------- */

function Property() {
    const [propertyId, setPropertyId] = useState(null);
    const [property, setProperty] = useState({});
    const [editData, setEditData] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingImage, setLoadingImage] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [message, setMessage] = useState("");

    /* ---------------- LOAD PROPERTY ---------------- */

    useEffect(() => {
        const loadProperty = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) return;

            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (!roleSnap.exists()) return;

            const pid = roleSnap.data().propertyId;
            setPropertyId(pid);

            const propSnap = await getDoc(doc(db, "properties", pid));
            if (propSnap.exists()) {
                setProperty(propSnap.data());
                setEditData({ ...propSnap.data(), country: "India" });
            }
            setLoading(false);
        };

        loadProperty();
    }, []);

    /* ---------------- IMAGE UPLOAD ---------------- */

    const uploadImage = async (file, field) => {
        if (!file || !propertyId) return;

        try {
            setLoadingImage(true);

            const storageRef = ref(
                storage,
                `property/${propertyId}/${field}`
            );

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            // cache busting
            const finalUrl = `${url}?t=${Date.now()}`;

            await updateDoc(doc(db, "properties", propertyId), {
                [field]: finalUrl,
            });

            setProperty(prev => ({ ...prev, [field]: finalUrl }));
            setEditData(prev => ({ ...prev, [field]: finalUrl }));
            setMessage("Image updated successfully!");

        } catch (err) {
            console.error(err);
            setMessage("Image upload failed!");
        } finally {
            setLoadingImage(false);
        }
    };

    /* ---------------- SAVE DETAILS ---------------- */

    const saveDetails = async () => {
        try {
            setSaving(true);
            await updateDoc(doc(db, "properties", propertyId), editData);
            setProperty(editData);
            setMessage("Profile updated successfully!");
            setOpenEdit(false);
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Typography sx={{ p: 4, color: colors.vanilla, bgcolor: colors.darkGreen, minHeight: '100vh' }}>Loading...</Typography>;

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                {message && <Alert sx={{ mb: 2, bgcolor: colors.calPolyGreen, color: colors.vanilla, border: `1px solid ${colors.goldenrod}` }}>{message}</Alert>}

                {(loadingImage || saving) && (
                    <Box sx={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(6, 54, 45, 0.8)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                        <CircularProgress size={70} sx={{ color: colors.gold }} />
                    </Box>
                )}

                {/* COVER IMAGE */}
                <Box sx={{ height: 350, position: "relative", borderRadius: 3, overflow: "hidden", border: `1px solid ${colors.goldenrod}` }}>
                    <img
                        src={property.buildingImage || "https://via.placeholder.com/1200x400"}
                        alt="cover"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />

                    <Button
                        component="label"
                        startIcon={<CameraAltIcon />}
                        sx={{ 
                            position: "absolute", 
                            bottom: 20, 
                            right: 20, 
                            bgcolor: colors.darkGreen, 
                            color: colors.vanilla, 
                            border: `1px solid ${colors.goldenrod}`,
                            '&:hover': { bgcolor: colors.calPolyGreen } 
                        }}
                    >
                        Change Cover
                        <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                                uploadImage(e.target.files[0], "buildingImage")
                            }
                        />
                    </Button>
                </Box>

                {/* PROFILE CARD */}
                <Paper sx={{ width: "70%", mx: "auto", mt: -10, p: 4, borderRadius: 4, bgcolor: colors.calPolyGreen, color: colors.vanilla, border: `1px solid ${colors.goldenrod}`, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <Box sx={{ position: "relative", width: 120, mx: "auto", mt: -8 }}>
                        <IconButton
                            component="label"
                            sx={{ p: 0 }}
                        >
                            <Avatar
                                src={property.logo || ""}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    border: `4px solid ${colors.darkGreen}`,
                                }}
                            />
                            <Box sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                bgcolor: colors.goldenrod,
                                borderRadius: "50%",
                                p: 0.5,
                                border: `2px solid ${colors.darkGreen}`
                            }}>
                                <CameraAltIcon sx={{ color: colors.darkGreen, fontSize: 18 }} />
                            </Box>
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                    uploadImage(e.target.files[0], "logo")
                                }
                            />
                        </IconButton>
                    </Box>

                    <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: colors.gold }}>
                            {property.type} - {property.hotelname}
                        </Typography>

                        <Typography sx={{ color: colors.vanilla, opacity: 0.9, mt: 1 }}>
                            {property.address}, {property.city}, {property.state} - {property.pincode}
                        </Typography>
                        <Typography sx={{ color: colors.vanilla, opacity: 0.9 }}>{property.mobile}</Typography>
                        <Typography sx={{ color: colors.vanilla, opacity: 0.9 }}>{property.email}</Typography>

                        <Button
                            startIcon={<EditIcon />}
                            sx={{ 
                                mt: 2, 
                                bgcolor: colors.goldenrod, 
                                color: colors.darkGreen, 
                                fontWeight: 700, 
                                textTransform: "none", 
                                borderRadius: "8px",
                                '&:hover': { bgcolor: colors.gold } 
                            }}
                            variant="contained"
                            onClick={() => setOpenEdit(true)}
                        >
                            Edit Profile
                        </Button>
                    </Box>
                </Paper>

                {/* EDIT DIALOG */}
                <Dialog 
                    open={openEdit} 
                    onClose={() => setOpenEdit(false)} 
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: "16px",
                            boxShadow: "0 24px 50px rgba(0,0,0,0.5)",
                            border: `1px solid ${colors.goldenrod}`,
                            bgcolor: colors.calPolyGreen,
                            color: colors.vanilla,
                            overflow: "hidden"
                        }
                    }}
                >
                    <DialogTitle sx={{ bgcolor: colors.darkGreen, color: colors.vanilla, fontWeight: 700 }}>Edit Property</DialogTitle>
                    <DialogContent dividers sx={{ borderColor: colors.goldenrod, opacity: 0.9, p: 3, bgcolor: colors.calPolyGreen }}>
                        <TextField 
                            label="Hotel Name" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.hotelname || ""}
                            onChange={(e) => setEditData({ ...editData, hotelname: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                } 
                            }}
                        />

                        <TextField 
                            label="Email" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.email || ""}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                } 
                            }}
                        />

                        <TextField 
                            label="Mobile" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.mobile || ""}
                            onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                } 
                            }}
                        />

                        <TextField 
                            label="Address" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.address || ""}
                            onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                } 
                            }}
                        />

                        <TextField 
                            label="City" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.city || ""}
                            onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                } 
                            }}
                        />

                        <TextField 
                            select 
                            label="State" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.state || ""}
                            onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                color: colors.vanilla,
                                backgroundColor: colors.darkGreen,
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                },
                                '& .MuiSelect-select': { color: colors.vanilla },
                                '& .MuiSvgIcon-root': { color: colors.vanilla }
                            }}
                        >
                            {INDIAN_STATES.map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </TextField>

                        <TextField 
                            label="Country" 
                            fullWidth 
                            size="small" 
                            margin="dense" 
                            value="India" 
                            disabled 
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod }
                                } 
                            }}
                        />

                        <TextField 
                            label="Pincode" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.pincode || ""}
                            onChange={(e) => setEditData({ ...editData, pincode: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            InputProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    backgroundColor: colors.darkGreen,
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&:hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                } 
                            }}
                        />

                        <TextField 
                            select 
                            label="Property Type" 
                            fullWidth 
                            size="small" 
                            margin="dense"
                            value={editData.type || ""}
                            onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                            InputLabelProps={{ style: { color: colors.vanilla } }}
                            sx={{ 
                                color: colors.vanilla,
                                backgroundColor: colors.darkGreen,
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-root": { 
                                    borderRadius: "8px",
                                    "& fieldset": { borderColor: colors.goldenrod },
                                    "&hover fieldset": { borderColor: colors.gold },
                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                },
                                '& .MuiSelect-select': { color: colors.vanilla },
                                '& .MuiSvgIcon-root': { color: colors.vanilla }
                            }}
                        >
                            {PROPERTY_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>

                    <DialogActions sx={{ p: 2.5, bgcolor: colors.darkGreen, gap: 1 }}>
                        <Button onClick={() => setOpenEdit(false)} sx={{ color: colors.vanilla, textTransform: "none", fontWeight: 700 }}>Cancel</Button>
                        <Button 
                            variant="contained" 
                            onClick={saveDetails}
                            sx={{
                                bgcolor: colors.goldenrod, 
                                color: colors.darkGreen, 
                                fontWeight: 700, 
                                textTransform: "none", 
                                borderRadius: "8px",
                                '&:hover': { bgcolor: colors.gold }
                            }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}

export default Property;